import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    SelectChangeEvent,
    Typography,
    Stack,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { useApi } from '@fnol/library/src/hooks';
import {
    ComboBox,
    DatePicker,
    InfoText,
    PhoneInputField,
    UCNField,
    AddressComboBox,
} from '@fnol/library/src/components';
import {
    GenericObject,
    getApplicationId,
    getBirthDateFromUCN,
    getIsPortal,
    getIsSalesForce,
    getIsWebsite,
    hasValueAndIsSalesForce,
    nameMaxLength,
    question50MaxLength,
    question10MaxLength,
    questionMaxLength,
    surnameMaxLength,
    ucnMaxLength,
} from '@fnol/library/src/lib';
import { constValues, legalPersonType, personType, question25MaxLength, translations } from '../../lib/consts';
import { prepareStreetNames } from '../../lib/helpers';
import { getStreetNames, getContactData } from '../../rpc/actions';

type IndividualProps = {
    individualType: personType;
    id?: number;
};

const Individual = ({ individualType, id }: IndividualProps) => {
    const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<GenericObject>();
    const [isFirstPass, setIsFirstPass] = useState(true);

    const name = `${personType[individualType]}IndividualName`;
    const lastname = `${personType[individualType]}IndividualLastName`;
    const ucn = `${personType[individualType]}IndividualUCN`;
    const passportNumber = `${personType[individualType]}IndividualPassportNumber`;
    const dateOfBirth = `${personType[individualType]}IndividualDateOfBirth`;
    const country = `${personType[individualType]}IndividualCountry`;
    const countryText = `${personType[individualType]}IndividualMyCountry`;
    const city = `${personType[individualType]}IndividualCity`;
    const cityInSerbia = `${personType[individualType]}IndividualCityInSerbia`;
    const address = `${personType[individualType]}IndividualAddress`;
    const addressNumber = `${personType[individualType]}IndividualAddressNumber`;
    const addressApartment = `${personType[individualType]}IndividualAddressApartment`;
    const phone = `${personType[individualType]}IndividualPhone`;
    const email = `${personType[individualType]}IndividualEmail`;
    const accountNumber = `${personType[individualType]}IndividualAccountNumber`;
    const duplicatedUcn = `${personType[individualType]}DuplicatedUcn`;

    const isIndividualSubmitterOnWebsite =
        values.jbkProdukcija === true &&
        individualType === personType.submitter &&
        values.submitterType === legalPersonType.individual &&
        getIsWebsite(getApplicationId());

    let cityCodeForStreetSearch = '';
    if (values[cityInSerbia] && values[cityInSerbia] !== '' && !!values[cityInSerbia].code)
        cityCodeForStreetSearch = values[cityInSerbia].code;

    const saveStreetNames = (res: GenericObject) => {
        const streetNames = prepareStreetNames(res.data);
        setFieldValue('streetNames', streetNames);
    };
    const { response: streetNames, fetchMethod: fetchStreetNames } = useApi(
        () => getStreetNames(cityCodeForStreetSearch),
        saveStreetNames,
    );

    const ucnOnBlur = async (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        checkIfUCNExists(e);
        //if it is not via website, we shall check if contact data exists for this UCN and if it does,
        //  we shall prefill the mobile and email fields with that data.
        //  This is only for Serbia, as UCN is only valid for Serbia.
        if (!getIsWebsite(getApplicationId()) && personType[individualType] === 'submitter') {
            const contactData = await getContactData(e.target.value);
            if (contactData.data) {
                if (contactData.data.telefon && contactData.data.telefon !== '') {
                    setFieldValue('submitterIndividualPhone', contactData.data.telefon);
                }
                if (contactData.data.email && contactData.data.email !== '') {
                    setFieldValue('submitterIndividualEmail', contactData.data.email);
                }
            }
        }
    };

    const setBirthDate = () => {
        const birthDate = getBirthDateFromUCN(values[ucn]);
        if (!!birthDate && values[country] == constValues.countrySerbiaId) {
            setFieldValue(dateOfBirth, birthDate);
        } else if (!birthDate && values[country] == constValues.countrySerbiaId) {
            setFieldValue(dateOfBirth, null);
        }
    };

    useEffect(() => {
        setBirthDate();
    }, [values[ucn]]);

    const checkIfUCNExists = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const insertTypes: number[] = [personType.submitter, personType.insured, personType.user];
        const userIds = [1, 2, 3, 4, 5];
        const insertedUcns: GenericObject = {};

        insertTypes.map((insertType) => {
            if (insertType === personType.user) {
                userIds.map((userId) => {
                    if (userId !== id) {
                        // skip id that is edited for injured persons (multiple persons can be entered)
                        insertedUcns[`${personType[insertType]}${userId}IndividualUCN`] =
                            values[`${personType[insertType]}${userId}IndividualUCN`];
                    }
                });
            } else if (insertType !== individualType) {
                // skip current type of person (only one person can be entered)
                insertedUcns[`${personType[insertType]}IndividualUCN`] =
                    values[`${personType[insertType]}IndividualUCN`];
            }
        });

        let duplicatedUcnExists = false;

        for (const [key, value] of Object.entries(insertedUcns)) {
            if (e.target?.value && e.target?.value !== '' && value === e.target?.value) {
                duplicatedUcnExists = true;
            }
        }

        setFieldValue(duplicatedUcn, duplicatedUcnExists);
    };

    // selected country textual representation
    const selectedIndividualCountry = values.preparedCountries?.find(
        (item: GenericObject) => item.id === parseInt(values[country] as string),
    );

    values[countryText] = selectedIndividualCountry?.text;

    const handleChangeCountry = (event: SelectChangeEvent<HTMLSelectElement>) => {
        setFieldValue(country, event.target.value.toString());
        setFieldValue(address, '');
        setFieldValue(addressNumber, '');
        setFieldValue(addressApartment, '');
        setFieldValue(passportNumber, '');
        setFieldValue(city, '');
        setFieldValue(cityInSerbia, '');
        setFieldValue(ucn, '');
        setFieldValue(dateOfBirth, null);
        setFieldValue(duplicatedUcn, false);
        if (individualType === personType.submitter) {
            setFieldValue('addressObjectSameAsSubmitter', '');
            setFieldValue('objectCity', '');
            setFieldValue('objectStreet', '');
            setFieldValue('objectStreetNumber', '');
        }
    };

    useEffect(() => {
        if (isFirstPass) {
            setIsFirstPass(false);
        } else if (values[cityInSerbia] && values[cityInSerbia] !== '' && !!values[cityInSerbia].code) {
            setFieldValue(address, '');
            fetchStreetNames();
        }
    }, [values[cityInSerbia]]);

    return (
        <>
            <FormControl>
                <Field
                    name={name}
                    id={name}
                    label={translations.personalInfo.NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: nameMaxLength,
                    }}
                    error={!!(touched[name] && errors[name])}
                    required
                />
                {touched[name] && errors[name] ? (
                    <FormHelperText error={true}>
                        <>{errors[name]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={lastname}
                    id={lastname}
                    label={translations.personalInfo.LAST_NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: surnameMaxLength,
                    }}
                    error={!!(touched[lastname] && errors[lastname])}
                    required
                />
                {touched[lastname] && errors[lastname] ? (
                    <FormHelperText error={true}>
                        <>{errors[lastname]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl error={!!(touched[country] && errors[country])} required>
                <InputLabel id={country + '-label'}>{translations.COUNTRY}</InputLabel>
                <Select
                    id={country}
                    labelId={country + '-label'}
                    name={country}
                    value={values[country] || ''}
                    label={translations.COUNTRY}
                    onChange={handleChangeCountry}
                >
                    {values.countries?.map((option: GenericObject) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
            {values[country].toString(10) !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId ? (
                <FormControl>
                    <Field
                        name={city}
                        id={city}
                        label={translations.CITY}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[city] && errors[city])}
                        required
                    />
                    {touched[city] && errors[city] ? (
                        <FormHelperText error={true}>
                            <>{errors[city]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            ) : (
                <FormControl>
                    <Field
                        name={cityInSerbia}
                        id={cityInSerbia}
                        label={translations.CITY}
                        options={
                            values.cities || [
                                {
                                    id: '',
                                    value: '',
                                    text: '',
                                    code: '',
                                },
                            ]
                        }
                        onChange={handleChange}
                        defaultValue={values[cityInSerbia]}
                        as={ComboBox}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[cityInSerbia] && errors[cityInSerbia])}
                        required
                    />
                    {touched[cityInSerbia] && errors[cityInSerbia] ? (
                        <FormHelperText error={true}>
                            <>{errors[cityInSerbia]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            {values[country].toString(10) !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId ? (
                <FormControl>
                    <Field
                        name={address}
                        id={address}
                        label={translations.ADDRESS}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[address] && errors[address])}
                        required
                    />
                    {touched[address] && errors[address] ? (
                        <FormHelperText error={true}>
                            <>{errors[address]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            ) : (
                <FormControl>
                    <Field
                        name={address}
                        id={address}
                        label={translations.ADDRESS}
                        options={
                            values.streetNames || [
                                {
                                    id: '',
                                    value: '',
                                    label: '',
                                },
                            ]
                        }
                        defaultValue={values[address]}
                        as={AddressComboBox}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[address] && errors[address])}
                        required
                    />
                    {touched[address] && errors[address] ? (
                        <FormHelperText error={true}>
                            <>{errors[address]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            <FormControl>
                <Field
                    name={addressNumber}
                    id={addressNumber}
                    label={translations.ADDRESS_NO}
                    as={TextField}
                    inputProps={{
                        maxLength: question10MaxLength,
                    }}
                    error={!!(touched[addressNumber] && errors[addressNumber])}
                    required
                />
                {touched[addressNumber] && errors[addressNumber] ? (
                    <FormHelperText error={true}>
                        <>{errors[addressNumber]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={addressApartment}
                    id={addressApartment}
                    label={translations.APARTMENT_NO}
                    as={TextField}
                    inputProps={{
                        maxLength: question10MaxLength,
                    }}
                />
            </FormControl>

            {values[country] === constValues.countrySerbiaId && (
                <FormControl>
                    <Field
                        name={ucn}
                        id={ucn}
                        label={translations.personalInfo.UCN}
                        as={UCNField}
                        inputProps={{
                            maxLength: ucnMaxLength,
                        }}
                        onBlur={ucnOnBlur}
                        error={!!(touched[ucn] && errors[ucn])}
                        required
                    />
                    {touched[ucn] && errors[ucn] ? (
                        <FormHelperText error={true}>
                            <>{errors[ucn]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            {values[duplicatedUcn] && (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5} id={duplicatedUcn}>
                        {translations.validationMessages.ALREADY_HAS_UCN}
                    </Typography>
                </Stack>
            )}

            {values[country] !== constValues.countrySerbiaId && (
                <FormControl>
                    <Field
                        name={passportNumber}
                        id={passportNumber}
                        label={translations.PASSPORT_NUMBER}
                        as={TextField}
                        inputProps={{
                            maxLength: question25MaxLength,
                        }}
                        error={!!(touched[passportNumber] && errors[passportNumber])}
                        required
                    />
                    {touched[passportNumber] && errors[passportNumber] ? (
                        <FormHelperText error={true}>
                            <>{errors[passportNumber]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            {values[country] !== constValues.countrySerbiaId && (
                <FormControl error={!!(touched[dateOfBirth] && errors[dateOfBirth])} className="policyDatePicker">
                    <Field
                        name={dateOfBirth}
                        id={dateOfBirth}
                        label={translations.DATE_OF_BIRTH}
                        as={DatePicker}
                        disableFuture
                        error={!!(touched[dateOfBirth] && errors[dateOfBirth])}
                    />
                    {touched[dateOfBirth] && errors[dateOfBirth] ? (
                        <FormHelperText error={true}>
                            <>{errors[dateOfBirth]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            {getIsSalesForce(getApplicationId()) && (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5}>
                        {translations.validationMessages.CONTACT_REQUIRED}
                    </Typography>
                </Stack>
            )}

            <FormControl>
                <PhoneInputField
                    name={phone}
                    id={phone}
                    specialLabel={
                        hasValueAndIsSalesForce(values[email])
                            ? translations.personalInfo.MOBILE
                            : translations.personalInfo.MOBILE_STAR
                    }
                    country={'rs'}
                    required={hasValueAndIsSalesForce(values[email]) ? false : true}
                />
            </FormControl>

            <FormControl>
                <Field
                    name={email}
                    id={email}
                    label={translations.personalInfo.EMAIL}
                    type="email"
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    error={!!(touched[email] && errors[email])}
                    required={hasValueAndIsSalesForce(values[phone]) && values[phone]?.length > 4 ? false : true}
                />
                {touched[email] && errors[email] ? (
                    <FormHelperText error={true}>
                        <>{errors[email]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            {isIndividualSubmitterOnWebsite && <InfoText label={translations.INDIVIDUAL_CONTACT_INFO_VERIFICATION} />}

            {getIsPortal(getApplicationId()) && individualType === personType.submitter && (
                <FormControl>
                    <Field
                        name={accountNumber}
                        label={translations.ACCOUNT_NUM}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[accountNumber] && errors[accountNumber])}
                        value={values[accountNumber]}
                        required
                    />
                    {touched[accountNumber] && errors[accountNumber] ? (
                        <FormHelperText error={true}>
                            <>{errors[accountNumber]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}
        </>
    );
};

export const IndividualCleanup = (
    type: personType,
    setFieldValue: (field: string, value: string | number, shouldValidate?: boolean | undefined) => void,
    userId?: number,
) => {
    const idUser = userId ? userId : '';
    setFieldValue(`${personType[type]}${idUser}IndividualName`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualLastName`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualUCN`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualDateOfBirth`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualEmail`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualPhone`, constValues.dialCodeSerbia);
    setFieldValue(`${personType[type]}${idUser}IndividualCountry`, constValues.countrySerbiaId);
    setFieldValue(`${personType[type]}${idUser}IndividualMyCountry`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualAddress`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualAddressNumber`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualAddressApartment`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualPassportNumber`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualCity`, '');
    setFieldValue(`${personType[type]}${idUser}IndividualCityInSerbia`, '');
};

export default Individual;
